import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'
import User from 'singletons/User'
import { DASHBOARD_VISITS, ACCOUNT_CHANGE_PASSWORD } from 'constants/routing'
import LicensesList from './components/LicensesList'
import NameBlock from './components/NameBlock'
import SectionList from '../../common/SectionList'
import SectionRow from '../../common/SectionRow'
import RowsContent from '../../common/RowsContent'
import { getProviderGeneralRows, getProviderProfileRows } from 'util/getProviderAccountRows'
import { useCallback, useMemo } from 'react'
import PencilIcon from '@ui-components-3/icons/lib/solid/Pencil'

const EditableLeftContent = ({ title, onEditClick }) => {
  return (
    <div className="flex flex-col items-start md:items-end">
      <h2 className="typography-body-xxl text-primary-800 md:text-right">{title}</h2>
      <button type="button" className="btn btn-neutral-borderless px-0" onClick={onEditClick}>
        <PencilIcon aria-hidden="true" />
        Edit
      </button>
    </div>
  )
}

const SecurityRightContent = () => {
  return (
    <div className="flex flex-col items-start gap-2">
      <p className="typography-caption">Password</p>
      <Link to={ACCOUNT_CHANGE_PASSWORD} className="btn btn-primary-outlined">
        Change password
      </Link>
    </div>
  )
}

type ViewPageProps = {
  onEditModeChange: (mode: 'profile' | 'general') => void
}

const ViewPage = ({ onEditModeChange }: ViewPageProps) => {
  const { provider } = User

  const generalRows = useMemo(() => getProviderGeneralRows(provider), [provider])

  const profileRows = useMemo(() => getProviderProfileRows(provider), [provider])

  const toggleEditMode = useCallback(
    (mode) => () => {
      onEditModeChange?.(mode)
    },
    [onEditModeChange],
  )

  if (!provider) {
    return null
  }

  return (
    <div>
      <SectionList>
        <SectionRow rightContent={<NameBlock firstName={provider.firstName} lastName={provider.lastName} />} />
        <SectionRow
          leftContent={<EditableLeftContent title="General" onEditClick={toggleEditMode('general')} />}
          rightContent={<RowsContent rows={generalRows} />}
        />
        {provider.profileExist && (
          <SectionRow
            leftContent={<EditableLeftContent title="Profile" onEditClick={toggleEditMode('profile')} />}
            rightContent={<RowsContent rows={profileRows} />}
          />
        )}
        <SectionRow leftContent={'Licenses'} rightContent={<LicensesList licenses={provider.licenses} />} />
        <SectionRow
          leftContent={`Past ${provider.isSuccessCoachProvider ? 'Sessions' : 'Visits'}`}
          rightContent={
            <Link to={DASHBOARD_VISITS} className="btn btn-primary-outlined">
              View {provider.isSuccessCoachProvider ? 'Sessions' : 'Visits'}{' '}
            </Link>
          }
        />
        <SectionRow leftContent={'Security'} rightContent={<SecurityRightContent />} />
      </SectionList>
    </div>
  )
}

export default observer(ViewPage)
